import { ActionIcon, Button, Tooltip } from '@mantine/core';
import { LoadingAnimation, sizes } from '@uag/react-core';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { SettingModelSettingType } from 'api/models';
import { PlacementSolutionRow } from 'app/modules/wallforming/components/setting-container/automated-placement/PlacementSolutionRow';
import {
    getSolutionKey,
    usePlacementSolutionHandler
} from 'app/modules/wallforming/components/setting-container/hooks/usePlacementSolutionHandler';

interface PlacementSolutionListProps {
    isLoading: boolean;
    settingType: SettingModelSettingType | undefined;
}

export const PlacementSolutionList = ({ isLoading, settingType }: PlacementSolutionListProps) => {
    const { t } = useTranslation();
    const {
        solutions,
        selectedSolution,
        showPlacementPreview,
        appliedKey,
        solutionItemRefs,
        handleOnApplySolution,
        handleOnReset,
        handleOnDoubleClick,
        handleOnPointerDown,
        setShowPlacementPreview
    } = usePlacementSolutionHandler();

    const solutionsCount = useMemo(() => solutions?.length ?? 0, [solutions]);

    const handleOnRegisterRef = useCallback(
        (element: HTMLDivElement | null, index: number) => {
            if (element) {
                solutionItemRefs.current.set(index, element);
            } else {
                solutionItemRefs.current.delete(index);
            }
        },
        [solutionItemRefs]
    );

    if (!solutions) {
        return null;
    }

    const hasAppliedSolution = appliedKey !== null;

    return (
        <section className="flex h-full min-h-0 min-w-0 flex-col gap-1 overflow-hidden">
            <div className="mb-1 flex min-w-30 shrink-0 items-center justify-between gap-2 text-xs font-semibold text-text-gray-dark">
                <span className="min-w-0 shrink-0 whitespace-nowrap text-xs font-semibold uppercase">
                    {t('labelSolutions')} ({solutionsCount})
                </span>
                <Tooltip label={showPlacementPreview ? t('labelHidePreview') : t('labelShowPreview')}>
                    <ActionIcon
                        aria-label={showPlacementPreview ? t('labelHidePreview') : t('labelShowPreview')}
                        className="bg-background-gray-ultra-light hover:bg-background-gray! pointer-events-auto action-icon-medium"
                        disabled={selectedSolution === null}
                        onPointerDown={() => setShowPlacementPreview(!showPlacementPreview)}
                    >
                        {showPlacementPreview ? 'visibility' : 'visibility_off'}
                    </ActionIcon>
                </Tooltip>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                {isLoading && <LoadingAnimation text={t('loading')} />}

                {!isLoading && solutionsCount > 0 && (
                    <div className="flex min-h-0 flex-1 flex-col gap-1 overflow-auto pr-1 pb-1">
                        {solutions.map((solution, index) => {
                            const solutionKey = getSolutionKey(index);
                            const isApplied = appliedKey === solutionKey;

                            return (
                                <PlacementSolutionRow
                                    key={`${solution.articleNumber}-${index}`}
                                    handleOnDoubleClick={(e) => handleOnDoubleClick(e, index)}
                                    handleOnRegisterRef={(element) => handleOnRegisterRef(element, index)}
                                    handleOnSelect={() => handleOnPointerDown(index)}
                                    hasAppliedSolution={hasAppliedSolution}
                                    index={index}
                                    isApplied={isApplied}
                                    isSelected={selectedSolution === index}
                                    settingType={settingType}
                                    solution={solution}
                                />
                            );
                        })}
                    </div>
                )}

                {!isLoading && solutionsCount === 0 && (
                    <span className="text-[13px] text-text-light">{t('labelNoSolutions')}</span>
                )}
            </div>

            <div className="mt-1 flex min-w-40 shrink-0 flex-wrap items-center gap-2">
                <Button
                    disabled={selectedSolution === null || appliedKey === null}
                    size={sizes.button.small}
                    variant="secondary"
                    onPointerDown={handleOnReset}
                >
                    {t('reset')}
                </Button>

                <Button
                    disabled={selectedSolution === null || appliedKey === getSolutionKey(selectedSolution)}
                    id="automated-corner-apply"
                    size={sizes.button.small}
                    onPointerDown={() => handleOnApplySolution(selectedSolution)}
                >
                    {t('apply')}
                </Button>
            </div>
        </section>
    );
};
